import React, { useContext } from 'react'

import { Box, DisplayPosition } from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import type { RecipeGridContentBlock } from '@library/api-menu-experience'
import { RecipeGrid } from '@library/menu-page'
import type { RecipeUUID } from '@library/menu-service'

import {
  WantToSeeMore,
  getClickAllRecipesTrackingAction,
} from 'routes/Menu/MenuRecipesPage/CTAToAllRecipes'
import {
  CaloriesTutorial,
  useShouldShowCaloriesTutorial,
} from 'routes/Menu/components/MenuPreferences'
import { CollectionId, CollectionSlug } from 'routes/Menu/domains/collections'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'

import { MenuBrandingContext } from '../menuBrandingContext'
import { useMenuExperienceCategoryNavigation } from '../menuExperienceNavigationContext'
import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'
import { useRecipeGridBasket } from './useRecipeGridBasket'
import { useRecipeGridCustomisationData } from './useRecipeGridCustomisationData'
import { useRecipeGridCustomisationEducation } from './useRecipeGridCustomisationEducation'

type RecipeGridContentBlockWrapperProps = {
  contentBlock: RecipeGridContentBlock
  collectionId?: string
}

export const RecipeGridContentBlockWrapper = ({
  contentBlock,
  collectionId,
}: RecipeGridContentBlockWrapperProps) => {
  const dispatch = useDispatch()
  const { textColor } = useContext(MenuBrandingContext)
  const { selectCategory } = useMenuExperienceCategoryNavigation()
  const { openRecipeDetail } = useMenuExperienceRecipeDetails()
  const shouldShowCaloriesTutorial = useShouldShowCaloriesTutorial(collectionId)
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const { getRecipeTileState, onAddClick, onRemoveClick } = useRecipeGridBasket(contentBlock.items)
  const { getRecipeCustomisationData, openRecipeCustomisations, closeRecipeCustomisations } =
    useRecipeGridCustomisationData({ collectionId })
  const { recipeIdForEducation, closeEducation } = useRecipeGridCustomisationEducation(contentBlock)

  const onRecipeClick = (recipeId: RecipeUUID, recipePosition: number) => {
    const recipe = contentBlock.items.find((item) => item.id === recipeId)

    if (!recipe) {
      return
    }

    openRecipeDetail({
      recipeId,
      title: recipe.title,
      position: recipePosition.toString(),
      source: 'recipe_grid',
    })
  }

  const onWantToSeeMoreClick = () => {
    selectCategory(
      {
        id: CollectionId.AllRecipes,
        slug: CollectionSlug.AllRecipes,
      },
      'recipe_grid',
    )
    dispatch(getClickAllRecipesTrackingAction())
  }

  const supplementaryItems = [
    ...(shouldShowCaloriesTutorial
      ? [
          {
            id: 'calories-tutorial',
            position: 5 as const,
            content: (
              <Box position={DisplayPosition.Relative} width="100%">
                <CaloriesTutorial />
              </Box>
            ),
          },
        ]
      : []),
    ...(collectionId === CollectionId.Recommendations
      ? [
          {
            id: 'want-to-see-more',
            position: 'end' as const,
            content: <WantToSeeMore onClick={onWantToSeeMoreClick} />,
          },
        ]
      : []),
  ]

  return (
    <RecipeGrid
      contentBlock={contentBlock}
      recipeCountTextColor={textColor}
      supplementaryItems={supplementaryItems}
      getRecipeTileState={getRecipeTileState}
      getRecipeCustomisationData={getRecipeCustomisationData}
      customisationEducationRecipeId={recipeIdForEducation}
      onRecipeClick={onRecipeClick}
      onAddClick={onAddClick}
      onRemoveClick={onRemoveClick}
      onBlockedAddAttempt={setBrowserCTAVisible}
      onCustomiseOpen={openRecipeCustomisations}
      onCustomiseClose={closeRecipeCustomisations}
      onCustomisationEducationClose={closeEducation}
    />
  )
}
